import { IncomingRequestCard } from '../../components/connections/IncomingConnections';
import { ConnectionPlanCard } from '../../components/connections/ConnectionPlanCard';
import { ConnectionOperationCard } from '../../components/connections/ConnectionOperationCard';
import { useHomeRefresh } from '../../components/email/useHomeRefresh';
import homeStyles from './Home.module.css';
import { ChatView } from '../../components/chat/ChatView';
import { ThinkingIndicator } from '../../components/chat/MessageList';
import { emailKey } from '../../lib/email-api';
import { HomeIntegrations } from '../../components/integrations/HomeIntegrations';
import { HomeSyncStatus } from '../../components/integrations/HomeSyncStatus';
import { ReplyOnboarding } from '../../components/email/ReplyOnboarding';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { GoalEditor } from '../../components/content/GoalEditor';
import { Icon } from '../../components/ui/Icon';
import { api } from '../../lib/api-client';
import type { ActionIntent } from '../../lib/api-types';
import { dailyBriefQuery, personalizationQuery, keys, useDecideActionIntent } from '../../lib/queries';
import styles from './Pages.module.css';

export function BriefPage() {
  return <BriefContent key={emailKey().join(':')} />;
}

function BriefContent() {
  const navigate = useNavigate();
  const openConversation = useCallback((conversationId: string) => {
    void navigate({ to: '/app/c/$conversationId', params: { conversationId }, replace: true });
  }, [navigate]);
  const [emailThreadId, setEmailThreadId] = useState<string>();
  const brief = useQuery(dailyBriefQuery());
  const personal = useQuery(personalizationQuery());
  const needsBusinessIntro = !personal.data?.goals.trim();
  const homeRefresh = useHomeRefresh();
  const client = useQueryClient();
  const markSeen = useMutation({ mutationFn: api.markBriefSeen,
    onSuccess: () => { void client.invalidateQueries({ queryKey: keys.dailyBrief }); },
  });
  const marked = useRef(false);
  useEffect(() => {
    if (!brief.data?.enabled || !brief.data.has_unseen || marked.current) return;
    marked.current = true; markSeen.mutate();
  }, [brief.data?.enabled, brief.data?.has_unseen, markSeen]);
  const content = brief.data?.brief?.content;
  const intents = brief.data?.action_intents ?? [];
  return <div className={`${styles.page} ${homeStyles.home}`}>
    <header className={styles.header}>
      <h1 className={styles.title}><span className={styles.titleIcon}><Icon name="sparkles" size={20} /></span>Home</h1>
      <button type="button" className={homeStyles.refresh} onClick={() => void homeRefresh.refresh().catch(() => {})} disabled={homeRefresh.refreshing}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
          <path d="M20 7a9 9 0 0 0-15-2L2 8m0-5v5h5M4 17a9 9 0 0 0 15 2l3-3m0 5v-5h-5" />
        </svg>
        {homeRefresh.refreshing ? 'Refreshing…' : 'Refresh'}
      </button>
    </header>
    <ChatView conversationId={null} onConversationSettled={openConversation}
      placeholder={needsBusinessIntro ? 'Tell Echo about your business…' : 'Ask Echo, write an email, or follow up…'}
      onRefresh={homeRefresh.refresh}
      onAutoProgressRefresh={homeRefresh.refresh}
      autoProgressStatus={<HomeSyncStatus progress={homeRefresh.progress} />}
      autoProgressSetup={needsBusinessIntro}
      composerInIntro={needsBusinessIntro}
      pageContext={{ path: '/app/brief', pageSpecificContext: { surface: 'home', overview: content, emailThreadId, goals: personal.data?.goals, currentMission: personal.data?.currentMission, nextStep: personal.data?.nextStepHint,
        ...(needsBusinessIntro ? { onboarding: 'The user has not set a goal yet. Help them get started through a warm, concise conversation. Build on any business information already in AI memory or this conversation; do not ask for it again. If the business is unknown, start by asking what their business does and who it helps. Ask one short question at a time, then help identify a concrete goal and first step. The application reviews each user turn to save useful business facts and agreed goals while preserving existing context; do not ask for a separate remember command or claim a change is saved before its receipt. Do not invent business details, run speculative research, or claim onboarding is complete until the goal is saved.' } : {}),
        instruction: 'The user is on Home. The goal, current mission, queued cards, and Auto Progress share this conversation. Example cards are a local walkthrough, never real email. emailThreadId identifies the selected queue card; email cards created in this conversation have their own thread IDs. Read get_email_thread before editing a saved draft. Email cards render directly in this conversation.' } }}
      intro={({ autoProgress, running, composer, setupNudge }) => <>
      <HomeIntegrations onThreadSelected={setEmailThreadId} />

      <ReplyOnboarding>
      {intents.length > 0 && <section className={styles.section}><h2 className={styles.sectionTitle}>Waiting for your approval</h2><div className={styles.stack}>{intents.map(intent => <ActionIntentCard key={intent.id} intent={intent} />)}</div></section>}
      <section className={`${homeStyles.mission}${needsBusinessIntro ? ` ${homeStyles.businessIntro}` : ''}`} aria-label={needsBusinessIntro ? 'Get started with Auto Progress' : 'Your mission'}>
        {needsBusinessIntro ? <>
          <h2>Tell me about your business.</h2>
          <p>Echo infers your goal as you chat, then gets to work setting things up for you.</p>
        </> : personal.data ? <GoalEditor personal={personal.data} />
          : <h2>{personal.error ? 'Your goals are unavailable.' : 'Loading your goals…'}</h2>}
        <button type="button" className={homeStyles.autoProgress} disabled={running || !personal.data || personal.isError} onClick={autoProgress}><Icon name="sparkles" size={20} />{running ? needsBusinessIntro ? 'Starting our conversation…' : 'Working on your next move…' : 'Auto Progress'}<span aria-hidden="true">→</span></button>
        {needsBusinessIntro && setupNudge && <SetupNudge key={setupNudge} text={setupNudge} />}
        {composer}
      </section>
      </ReplyOnboarding>
        {homeRefresh.error && <p role="alert">{homeRefresh.error} <button onClick={() => void homeRefresh.refresh().catch(() => {})}>Try again</button></p>}
    </>} />
  </div>;
}

function SetupNudge({ text }: { text: string }) {
  const [visibleWords, setVisibleWords] = useState(0);
  const words = text.split(' ');
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setVisibleWords(words.length);
      return;
    }
    setVisibleWords(0);
    let shown = 0;
    let timer: ReturnType<typeof setTimeout>;
    const reveal = () => {
      shown += 1;
      setVisibleWords(shown);
      if (shown < words.length) timer = setTimeout(reveal, /[.?!,]$/.test(words[shown - 1] ?? '') ? 150 : 65);
    };
    timer = setTimeout(reveal, 400);
    return () => clearTimeout(timer);
  }, [text, words.length]);
  const complete = visibleWords >= words.length;
  return <div className={homeStyles.setupNudge}>
    <div aria-hidden="true">
      {visibleWords === 0 ? <ThinkingIndicator /> : <p>{words.slice(0, visibleWords).join(' ')}{!complete && <span className={homeStyles.nudgeCursor} />}</p>}
    </div>
    <span className={homeStyles.nudgeAnnouncement} role="status">{complete ? text : ''}</span>
  </div>;
}

export function ActionIntentCard({ intent }: { intent: ActionIntent }) {
  if(intent.tool_name==='incoming_connection_request'&&typeof intent.payload.incomingRequestId==='string')return <IncomingRequestCard requestId={intent.payload.incomingRequestId} clientName={typeof intent.payload.client==='string'?intent.payload.client:undefined}/>;
  if(intent.tool_name==='invoke_connection_plan'&&typeof intent.payload.planId==='string')return <ConnectionPlanCard planId={intent.payload.planId}/>;
  if(intent.tool_name==='invoke_connection'&&typeof intent.payload.operationId==='string')return <ConnectionOperationCard operationId={intent.payload.operationId}/>;
  return <StandardActionIntentCard intent={intent}/>;
}
function StandardActionIntentCard({ intent }: { intent: ActionIntent }) {
  const decide = useDecideActionIntent();
  const pending = intent.status === 'pending_approval' || intent.status === 'failed';

  return (
    <article className={styles.intent}>
      <div className={styles.intentHead}>
        <span className={styles.intentTool}>{intent.tool_name}</span>
        <span className={styles.intentStatus} data-status={intent.status}>
          {intent.status.replace(/_/g, ' ')}
        </span>
      </div>
      {intent.summary ? <p className={styles.intentSummary}>{intent.summary}</p> : null}
      <pre className={styles.intentPayload}>{JSON.stringify(intent.payload, null, 2)}</pre>
      {pending ? (
        <div className={styles.intentActions}>
          <button
            type="button"
            className={styles.approve}
            disabled={decide.isPending}
            onClick={() => decide.mutate({ id: intent.id, decision: 'approve' })}
          >
            Approve
          </button>
          <button
            type="button"
            className={styles.reject}
            disabled={decide.isPending}
            onClick={() => decide.mutate({ id: intent.id, decision: 'reject' })}
          >
            Reject
          </button>
        </div>
      ) : null}
    </article>
  );
}
